import type {
  SystemCertificationMatrix,
  SystemCertificationProjection,
  SystemEvidenceProjection,
  SystemEvidenceRecord,
  SystemStatusProjection,
  SystemWorkspaceDefinition,
} from './SystemWorkspaceContracts';

const CRITICAL_CERTIFICATION_KEYS:
  Array<keyof SystemCertificationMatrix> = [
    'source',
    'build',
  ];

export function projectSystemEvidence(
  systemId: string,
  records: SystemEvidenceRecord[],
): SystemEvidenceProjection {
  const ownedRecords = records
    .filter(record => record.systemId === systemId)
    .sort((left, right) =>
      right.observedAt.localeCompare(
        left.observedAt,
      ),
    );

  return {
    systemId,
    records: ownedRecords,
    latestObservedAt:
      ownedRecords[0]?.observedAt ?? null,
    currentEvidenceAvailable:
      ownedRecords.length > 0,
    criticalFailureCount:
      ownedRecords.filter(
        record =>
          record.critical &&
          record.passed === false,
      ).length,
    failedEvidenceCount:
      ownedRecords.filter(
        record => record.passed === false,
      ).length,
    simulatedEvidenceCount:
      ownedRecords.filter(
        record =>
          record.mode === 'SIMULATED',
      ).length,
    liveEvidenceCount:
      ownedRecords.filter(
        record => record.mode === 'LIVE',
      ).length,
  };
}

export function projectSystemCertification(
  systemId: string,
  certification: SystemCertificationMatrix,
): SystemCertificationProjection {
  const entries = Object.entries(
    certification,
  ) as Array<
    [
      keyof SystemCertificationMatrix,
      SystemCertificationMatrix[
        keyof SystemCertificationMatrix
      ],
    ]
  >;

  const passedKeys = entries
    .filter(([, value]) => value === 'pass')
    .map(([key]) => key);

  const failedKeys = entries
    .filter(([, value]) => value === 'fail')
    .map(([key]) => key);

  const pendingKeys = entries
    .filter(([, value]) => value === 'pending')
    .map(([key]) => key);

  const notRunKeys = entries
    .filter(([, value]) => value === 'not_run')
    .map(([key]) => key);

  const requiredEntries = entries.filter(
    ([, value]) =>
      value !== 'not_applicable',
  );

  return {
    systemId,
    certification,
    passedKeys,
    failedKeys,
    pendingKeys,
    notRunKeys,
    requiredPassCount:
      requiredEntries.length,
    completedPassCount:
      requiredEntries.filter(
        ([, value]) => value === 'pass',
      ).length,
  };
}

export function projectSystemStatus(
  workspace: SystemWorkspaceDefinition,
): SystemStatusProjection {
  const certification =
    projectSystemCertification(
      workspace.id,
      workspace.certification,
    );

  const criticalCertificationFailures =
    CRITICAL_CERTIFICATION_KEYS.filter(
      key =>
        workspace.certification[key] ===
        'fail',
    );

  const generatedAt =
    new Date().toISOString();

  if (
    workspace.statusSignals
      .criticalFailure ||
    criticalCertificationFailures.length > 0
  ) {
    return {
      systemId: workspace.id,
      light: 'red',
      label: 'Blocked',
      reason:
        workspace.statusSignals.reason ||
        'A critical authority or certification gate failed.',
      blockingCertificationKeys:
        criticalCertificationFailures,
      generatedAt,
    };
  }

  if (
    workspace.statusSignals.repairRunning
  ) {
    return {
      systemId: workspace.id,
      light: 'purple',
      label: 'Repairing',
      reason:
        workspace.statusSignals.reason ||
        'An approved governed repair is running or awaiting verification.',
      blockingCertificationKeys: [],
      generatedAt,
    };
  }

  if (
    workspace.statusSignals
      .simulationRunning
  ) {
    return {
      systemId: workspace.id,
      light: 'blue',
      label: 'Simulating',
      reason:
        workspace.statusSignals.reason ||
        'A controlled system simulation is running.',
      blockingCertificationKeys: [],
      generatedAt,
    };
  }

  if (
    !workspace.statusSignals
      .currentEvidenceAvailable
  ) {
    return {
      systemId: workspace.id,
      light: 'gray',
      label: 'Unknown',
      reason:
        workspace.statusSignals.reason ||
        'No current evidence is available for this system.',
      blockingCertificationKeys: [],
      generatedAt,
    };
  }

  if (
    workspace.statusSignals
      .nonCriticalIssue ||
    certification.failedKeys.length > 0 ||
    certification.pendingKeys.length > 0 ||
    certification.notRunKeys.length > 0
  ) {
    return {
      systemId: workspace.id,
      light: 'orange',
      label: 'Degraded',
      reason:
        workspace.statusSignals.reason ||
        'The system has pending or noncritical certification work.',
      blockingCertificationKeys:
        certification.failedKeys,
      generatedAt,
    };
  }

  return {
    systemId: workspace.id,
    light: 'green',
    label: 'Healthy',
    reason:
      workspace.statusSignals.reason ||
      'All required system gates are passing.',
    blockingCertificationKeys: [],
    generatedAt,
  };
}
